<!-- ColorSizeLine.vue - Line showing selected color with size quantity inputs -->
<template>
  <div class="color-size-line" :class="{ 'is-qty-only': isQtyOnly }">
    <!-- Arrow for qty-only mode (positioned above the component) -->
    <div v-if="isQtyOnly && showArrow" class="qty-arrow-indicator">
      <div class="arrow-up"></div>
    </div>
    
    <div v-if="!isQtyOnly" class="line-header">
      <div class="color-info">
        <span class="color-name-label">{{ colorName.replace(/-/g, ' ')}} Quantity</span>
      </div>

    </div>

    <div class="sizes-container">
      <div 
        v-for="size in filteredSizes" 
        :key="size"
        class="size-column"
      >
        <div class="size-label">{{ getSizeLabel(size) }}</div>
        
        <!-- Використовуємо CustomQty замість controls -->
        <CustomQty
          :line="line"
          :imprintKey="imprintKey"
          :sizeTitle="size"
          :lineId="getLineIndex()"
          :qtyIncrement="qtyIncrement"
          :minOrderQty="minOrderQty"
          :prId="`qty-${line.id}-${size}`"
          @update-quantity="handleUpdateQuantity"
        />
        
        <div v-if="hasSurcharge(size)" class="surcharge">
          {{ getSurcharge(size) }}
        </div>
      </div>
    </div>
     <div class="btn-clear-wrap"> <button
              class="btn-clear"
              @click="handleClear"
              title="Clear this color"
      >
          Clear
      </button></div>
  </div>
</template>

<script>
import CustomQty from './CustomQty.vue'
import { useSelectionStore } from '@/features/imprint/stores/useSelectionStore'
import { useImprintStore } from "@/features/imprint/stores/useImprintStore";

export default {
  name: 'ColorSizeLine',

  components: {
    CustomQty
  },

  props: {
    line: {
      type: Object,
      required: true
    },
    availableSizes: {
      type: Array,
      required: true
    },
    imprintKey: {
      type: String,
      default: 'default-imprint'
    },
    qtyIncrement: {
      type: Number,
      default: 1
    },
    minOrderQty: {
      type: Number,
      default: 0
    },
    isQtyOnly: {
      type: Boolean,
      default: false
    },
    showArrow: {
      type: Boolean,
      default: false
    }
  },

 data() {
    return {
        imprintStore:useImprintStore()
    }
 },

  computed: {
    colorName() {
      return this.line.slectedColor?.title || 'Color'
    },

    // Filter available sizes based on color's attributes_relation_array
    filteredSizes() {
      const colorTitle = this.line.slectedColor?.title;

      if (!colorTitle) {
        console.warn('[ColorSizeLine] No color selected, showing all sizes');
        return this.availableSizes;
      }

      // If no sizes configured (qty only product), return as is
      if (this.availableSizes.length === 1 && this.availableSizes[0] === 'qty') {
        return this.availableSizes;
      }

      const fullJson = this.imprintStore.fullJson;
      const currentSku = this.imprintStore.selectedImprintMethod?.sku;

      if (!fullJson || !currentSku) {
        console.warn('[ColorSizeLine] No fullJson or SKU, showing all sizes');
        return this.availableSizes;
      }

      const attributesRelation = fullJson.configurable_array?.[currentSku]?.attributes_relation_array;
      
      if (!attributesRelation) {
        console.warn('[ColorSizeLine] No attributes_relation_array, showing all sizes');
        return this.availableSizes;
      }

      const colorRelation = attributesRelation[colorTitle];
      
      if (!colorRelation) {
        console.warn('[ColorSizeLine] No relation for color:', colorTitle, 'showing all sizes');
        return this.availableSizes;
      }

      // If colorRelation is not an array, show all sizes
      if (!Array.isArray(colorRelation)) {
        console.warn('[ColorSizeLine] colorRelation is not array for:', colorTitle, 'showing all sizes');
        return this.availableSizes;
      }

      // Filter sizes based on what's available and enabled for this color
      // Exclude variants with status 'Disabled' from the product feed
      const availableSizesForColor = colorRelation
        .filter(item => item.status !== 'Disabled')
        .map(item => item.size);
      const filtered = this.availableSizes.filter(size => availableSizesForColor.includes(size))

      const hiddenSizes = this.availableSizes.filter(size => !availableSizesForColor.includes(size))
      if (hiddenSizes.length) {
        console.log(`[VariantFilter] 🚫 Hidden sizes for "${colorTitle}" (${hiddenSizes.length}):`,
          colorRelation
            .filter(i => !availableSizesForColor.includes(i.size))
            .map(i => ({ size: i.size, status: i.status }))
        )
      }
      console.log(`[VariantFilter] ✅ Visible sizes for "${colorTitle}": [${filtered.join(', ')}]`)

      return filtered
    }
  },

  methods: {
    getSizeLabel(size) {
      return size === 'qty' ? 'Quantity' : size
    },

    getLineIndex() {
      // Знаходимо індекс line в store
      const selectionStore = useSelectionStore()
      const lines = selectionStore.getLinesByImprintKey(this.imprintKey)
      return lines.findIndex(l => l.id === this.line.id)
    },

    hasSurcharge(size) {
      if (size === 'qty') return false
      const surcharges = this.imprintStore?.sizeUpcharge || {}
      const sizes = Object.keys(surcharges);
      return sizes.includes(size)
    },

    getSurcharge(size) {
        const surcharges = this.imprintStore?.sizeUpcharge || {}
        const value = surcharges[size]

        if (value === undefined || value === null) {
            return ''
        }

        const formatted = Math.abs(value).toFixed(2)
        return value >= 0 ? `+$${formatted}` : `-$${formatted}`
    },

    handleUpdateQuantity(event) {
      // Пробросуємо подію далі
      this.$emit('update-quantity', event)
    },

    handleClear() {
      this.$emit('remove', this.line.id)
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/styles/components/color-size-line';
</style>
